import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../common/Button';
import LeadStatusBadge from './LeadStatusBadge';
import HeatLevelIndicator from './HeatLevelIndicator';
import Spinner from '../common/Spinner';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useAuth } from '../../context/AuthContext';

/**
 * LeadDetailPane Component
 *
 * Slide-out detail pane for viewing and editing lead information.
 * Features tabs for Details, History, and Notes.
 *
 * @param {Object} lead - Lead object with assignments
 * @param {boolean} isOpen - Whether the pane is open
 * @param {function} onClose - Close callback
 * @param {function} onEdit - Edit callback
 * @param {function} onUpdate - Update callback (refresh data)
 */
function LeadDetailPane({ lead, isOpen, onClose, onEdit, onUpdate }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('details');
  const [history, setHistory] = useState([]);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [submittingNote, setSubmittingNote] = useState(false);

  // Status update related states
  const [showAppointmentForm, setShowAppointmentForm] = useState(false);
  const [appointmentDate, setAppointmentDate] = useState(new Date());
  const [appointmentNotes, setAppointmentNotes] = useState('');
  const [appointmentAction, setAppointmentAction] = useState('appt_set');
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [bookingDate, setBookingDate] = useState(new Date());
  const [showBookingConfirmation, setShowBookingConfirmation] = useState(false);
  const [showDiscardConfirmation, setShowDiscardConfirmation] = useState(false);
  const [showResetToNewConfirmation, setShowResetToNewConfirmation] = useState(false);
  const [showDeleteNoteConfirmation, setShowDeleteNoteConfirmation] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);

  const latestAssignment = lead?.assignments?.[lead.assignments.length - 1];
  const status = latestAssignment?.status || 'new';

  // Check user role
  const isManager = user?.role === 'manager';
  const isLeader = user?.role === 'leader';

  // Fetch history when History tab is activated
  useEffect(() => {
    if (isOpen && lead && latestAssignment && activeTab === 'history') {
      fetchHistory();
    }
  }, [activeTab, isOpen, lead]);

  // Fetch notes when Notes tab is activated
  useEffect(() => {
    if (isOpen && lead && latestAssignment && activeTab === 'notes') {
      fetchNotes();
    }
  }, [activeTab, isOpen, lead]);

  // Reset state when pane closes
  useEffect(() => {
    if (!isOpen) {
      setActiveTab('details');
      setHistory([]);
      setNotes([]);
      setNewNote('');
    }
  }, [isOpen]);

  async function fetchHistory() {
    try {
      setLoadingHistory(true);
      const response = await api.get(`/leads/assignments/${latestAssignment.id}/history`);
      setHistory(response.data.history || []);
    } catch (error) {
      console.error('Failed to load history:', error);
      toast.error('Failed to load lead history');
    } finally {
      setLoadingHistory(false);
    }
  }

  async function fetchNotes() {
    try {
      setLoadingNotes(true);
      // Notes are typically included in the assignment - use latestAssignment.notes if available
      // Otherwise fetch from the lead endpoint
      if (latestAssignment.notes) {
        setNotes(latestAssignment.notes);
      } else {
        const response = await api.get(`/leads/${lead.id}`);
        const leadData = response.data.lead;
        const assignmentData = leadData.assignments?.find(a => a.id === latestAssignment.id);
        setNotes(assignmentData?.notes || []);
      }
    } catch (error) {
      console.error('Failed to load notes:', error);
      toast.error('Failed to load notes');
    } finally {
      setLoadingNotes(false);
    }
  }

  async function handleAddNote() {
    if (!newNote.trim()) {
      toast.warning('Please enter a note');
      return;
    }

    try {
      setSubmittingNote(true);
      await api.post(`/leads/assignments/${latestAssignment.id}/notes`, {
        content: newNote
      });
      toast.success('Note added successfully');
      setNewNote('');
      // Refresh notes list without changing tab
      await fetchNotes();
      // Don't call onUpdate here as it might cause tab to switch
    } catch (error) {
      console.error('Failed to add note:', error);
      toast.error('Failed to add note');
    } finally {
      setSubmittingNote(false);
    }
  }

  async function handleHeatLevelChange(level) {
    try {
      // Optimistically update the local state
      if (latestAssignment) {
        latestAssignment.heatLevel = level;
      }

      await api.patch(`/leads/assignments/${latestAssignment.id}/status`, {
        heatLevel: level
      });
      toast.success('Heat level updated');
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Failed to update heat level:', error);
      toast.error('Failed to update heat level');
      // Revert on error by triggering re-fetch
      if (onUpdate) onUpdate();
    }
  }

  // Status update handlers
  async function handleStatusUpdate(newStatus) {
    try {
      await api.patch(`/leads/assignments/${latestAssignment.id}/status`, {
        status: newStatus
      });
      toast.success('Lead status updated');
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error(error.message || 'Failed to update status');
    }
  }

  async function handleAppointmentSubmit() {
    if (!appointmentDate) {
      toast.warning('Please select an appointment date');
      return;
    }

    try {
      if (appointmentAction === 'reschedule_appointment') {
        await api.patch(`/leads/assignments/${latestAssignment.id}/status`, {
          action: 'reschedule_appointment',
          appointmentDate: appointmentDate.toISOString(),
          appointmentNotes
        });
        toast.success('Appointment rescheduled');
      } else {
        await api.patch(`/leads/assignments/${latestAssignment.id}/status`, {
          status: 'appt_set',
          appointmentDate: appointmentDate.toISOString(),
          appointmentNotes
        });
        toast.success('Appointment set successfully');
      }

      // Add appointment notes to the notes tab if provided
      if (appointmentNotes && appointmentNotes.trim()) {
        const noteContent = appointmentAction === 'reschedule_appointment'
          ? `Appointment Rescheduled:\n${appointmentNotes}`
          : `Appointment Set:\n${appointmentNotes}`;

        await api.post(`/leads/assignments/${latestAssignment.id}/notes`, {
          content: noteContent
        });
      }

      setShowAppointmentForm(false);
      setAppointmentNotes('');
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Failed to set appointment:', error);
      toast.error(error.message || 'Failed to set appointment');
    }
  }

  async function handlePendingBookingSubmit() {
    if (!bookingDate) {
      toast.warning('Please select a booking date');
      return;
    }

    try {
      await api.patch(`/leads/assignments/${latestAssignment.id}/status`, {
        status: 'pending_booking',
        bookingDate: bookingDate.toISOString()
      });
      toast.success('Lead marked as pending booking');
      setShowBookingForm(false);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Failed to set pending booking:', error);
      toast.error(error.message || 'Failed to set pending booking');
    }
  }

  async function handleResetToNew() {
    if (!isManager) {
      toast.error('Only managers can reset leads to NEW status');
      return;
    }

    try {
      await api.post(`/leads/assignments/${latestAssignment.id}/reset`);
      toast.success('Lead status reset to NEW');
      setShowResetToNewConfirmation(false);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Failed to reset lead:', error);
      toast.error(error.message || 'Failed to reset lead status');
      setShowResetToNewConfirmation(false);
    }
  }

  async function handleDeleteNote() {
    if (!noteToDelete) return;

    try {
      await api.delete(`/leads/assignments/${latestAssignment.id}/notes/${noteToDelete.id}`);
      toast.success('Note deleted successfully');
      setShowDeleteNoteConfirmation(false);
      setNoteToDelete(null);
      // Refresh notes list without changing tab
      await fetchNotes();
    } catch (error) {
      console.error('Failed to delete note:', error);
      toast.error(error.message || 'Failed to delete note');
      setShowDeleteNoteConfirmation(false);
      setNoteToDelete(null);
    }
  }

  // Helper functions for status transitions
  const getAvailableStatuses = (currentStatus, appointmentDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const apptDate = appointmentDate ? new Date(appointmentDate) : null;
    if (apptDate) {
      apptDate.setHours(0, 0, 0, 0);
    }

    const isAppointmentDayOrPassed = apptDate && apptDate <= today;

    const statusMap = {
      'new': ['contact_attempted', 'contact_made', 'appt_set'],
      'contact_attempted': ['contact_made', 'appt_set'],
      'contact_made': ['contact_attempted', 'appt_set'],
      'appt_set': isAppointmentDayOrPassed
        ? ['appt_held', 'appt_no_show', 'reschedule_appointment', 'cancel_appointment']
        : ['reschedule_appointment', 'cancel_appointment'],
      'appt_no_show': ['contact_attempted', 'contact_made', 'appt_set'],
      'appt_held': ['pending_booking', 'booked'],
      'pending_booking': ['booked', 'appt_held'],
      'cancel_appointment': ['contact_attempted', 'contact_made', 'appt_set']
    };
    return statusMap[currentStatus] || [];
  };

  const getStatusColor = (status) => {
    const colors = {
      new: '#dc2626',
      contact_attempted: '#f59e0b',
      contact_made: '#8b5cf6',
      appt_set: '#10b981',
      appt_no_show: '#ef4444',
      appt_held: '#14b8a6',
      pending_booking: '#f97316',
      booked: '#22c55e',
      form_submitted: '#059669',
      system_discard: '#6b7280',
      manual_discard: '#991b1b',
      cancel_appointment: '#6b7280',
      reschedule_appointment: '#8b5cf6'
    };
    return colors[status] || '#6b7280';
  };

  const getStatusLabel = (status) => {
    if (!status) return 'UNKNOWN';
    const labels = {
      new: 'NEW',
      contact_attempted: 'CONTACT ATTEMPTED',
      contact_made: 'CONTACT MADE',
      appt_set: 'APPT SET',
      appt_no_show: 'APPT NO SHOW',
      appt_held: 'APPT HELD',
      pending_booking: 'PENDING BOOKING',
      booked: 'BOOKED',
      form_submitted: 'FORM SUBMITTED',
      system_discard: 'SYSTEM DISCARD',
      manual_discard: 'MANUAL DISCARD',
      cancel_appointment: 'CANCEL APPOINTMENT',
      reschedule_appointment: 'RESCHEDULE'
    };
    return labels[status] || status.toUpperCase();
  };

  const getTodayWithTime = () => {
    return new Date();
  };

  const getTomorrowWithTime = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow;
  };

  const getNextWeekWithTime = () => {
    const nextWeek = new Date();
    nextWeek.setDate(nextWeek.getDate() + 7);
    return nextWeek;
  };

  if (!lead || !isOpen) return null;

  const tabs = [
    { id: 'details', label: 'Details', icon: '📋' },
    { id: 'history', label: 'History', icon: '📜' },
    { id: 'notes', label: 'Notes', icon: '📝' }
  ];

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 z-40 transition-opacity"
        onClick={onClose}
      ></div>

      {/* Side Panel */}
      <div className="fixed right-0 top-0 bottom-0 w-full md:w-2/3 lg:w-1/2 bg-white shadow-2xl z-50 overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-dark px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-xl font-display font-bold text-white">Lead Details</h2>
          <button
            onClick={onClose}
            className="text-white hover:text-tenq-gold transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="sticky top-[60px] bg-white border-b z-10">
          <div className="flex">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex-1 px-4 py-3 text-sm font-medium border-b-2 transition-colors
                  ${activeTab === tab.id
                    ? 'border-tenq-gold text-tenq-gold bg-tenq-gold bg-opacity-5'
                    : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                  }
                `}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 pb-24">
          {/* DETAILS TAB */}
          {activeTab === 'details' && (
            <>
              {/* Heat Level Section */}
              <section>
                <h3 className="text-lg font-semibold text-tenq-gold mb-4">Heat Level</h3>
                <HeatLevelIndicator
                  level={latestAssignment?.heatLevel}
                  onChange={handleHeatLevelChange}
                  size="lg"
                />
                <p className="text-xs text-gray-500 mt-2">
                  Rate this lead from 1 (cold) to 5 (hot) based on urgency and potential
                </p>
              </section>

              {/* Contact Information */}
              <section className="border-t pt-6">
                <h3 className="text-lg font-semibold text-tenq-gold mb-4">Contact Information</h3>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm text-gray-600">Name</label>
                    <p className="text-gray-900 font-medium">{lead.name || 'Unknown'}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Phone Number</label>
                    <p className="text-gray-900 font-medium">{lead.phoneNumber || 'No phone'}</p>
                    {lead.phoneNumber && (
                      <div className="flex gap-2 mt-2">
                        <a
                          href={`tel:+${lead.phoneNumber.replace(/[^0-9]/g, '')}`}
                          className="inline-flex items-center px-3 py-1 bg-tenq-gold text-white rounded text-sm hover:bg-opacity-90"
                        >
                          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"/>
                          </svg>
                          Call
                        </a>
                        <a
                          href={`https://wa.me/${lead.phoneNumber.replace(/[^0-9]/g, '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-opacity-90"
                        >
                          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                          </svg>
                          WhatsApp
                        </a>
                      </div>
                    )}
                  </div>
                  {lead.email && (
                    <div>
                      <label className="text-sm text-gray-600">Email</label>
                      <p className="text-gray-900">{lead.email}</p>
                    </div>
                  )}
                </div>
              </section>

              {/* Project Details */}
              <section className="border-t pt-6">
                <h3 className="text-lg font-semibold text-tenq-gold mb-4">Project Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-600">Property Type</label>
                    <p className="text-gray-900 uppercase">{lead.propertyType || '-'}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Project Type</label>
                    <p className="text-gray-900 capitalize">
                      {lead.projectType?.replace(/_/g, ' ') || '-'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Budget Range</label>
                    <p className="text-gray-900">
                      {lead.budgetMin && lead.budgetMax
                        ? `RM ${parseInt(lead.budgetMin).toLocaleString()} - ${parseInt(lead.budgetMax).toLocaleString()}`
                        : lead.budgetMin
                        ? `RM ${parseInt(lead.budgetMin).toLocaleString()}+`
                        : '-'
                      }
                    </p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Square Footage</label>
                    <p className="text-gray-900">{lead.squareFootage ? `${lead.squareFootage} sqft` : '-'}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Room Count</label>
                    <p className="text-gray-900">{lead.roomCount || '-'}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Preferred Style</label>
                    <p className="text-gray-900">{lead.preferredStyle || '-'}</p>
                  </div>
                  {lead.roomTypes && (
                    <div className="col-span-2">
                      <label className="text-sm text-gray-600">Room Types</label>
                      <p className="text-gray-900">{lead.roomTypes}</p>
                    </div>
                  )}
                  {lead.timeframe && (
                    <div className="col-span-2">
                      <label className="text-sm text-gray-600">Timeframe</label>
                      <p className="text-gray-900">{lead.timeframe}</p>
                    </div>
                  )}
                  {lead.address && (
                    <div className="col-span-2">
                      <label className="text-sm text-gray-600">Address</label>
                      <p className="text-gray-900">{lead.address}</p>
                    </div>
                  )}
                </div>
              </section>

              {/* Source Information */}
              <section className="border-t pt-6">
                <h3 className="text-lg font-semibold text-tenq-gold mb-4">Source Information</h3>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm text-gray-600">Platform</label>
                    <p className="text-gray-900 capitalize">{lead.platform || 'Manual'}</p>
                  </div>
                  {lead.campaign_name && (
                    <div>
                      <label className="text-sm text-gray-600">Campaign</label>
                      <p className="text-gray-900">{lead.campaign_name}</p>
                    </div>
                  )}
                  {lead.adset_name && (
                    <div>
                      <label className="text-sm text-gray-600">Ad Set</label>
                      <p className="text-gray-900">{lead.adset_name}</p>
                    </div>
                  )}
                  {lead.ad_name && (
                    <div>
                      <label className="text-sm text-gray-600">Ad</label>
                      <p className="text-gray-900">{lead.ad_name}</p>
                    </div>
                  )}
                </div>
              </section>

              {/* Status */}
              <section className="border-t pt-6">
                <h3 className="text-lg font-semibold text-tenq-gold mb-4">Current Status</h3>
                <LeadStatusBadge status={status} />
              </section>

              {/* Initial Notes */}
              {lead.initialNotes && (
                <section className="border-t pt-6">
                  <h3 className="text-lg font-semibold text-tenq-gold mb-4">Initial Notes</h3>
                  <p className="text-gray-700 whitespace-pre-wrap bg-gray-50 p-4 rounded">
                    {lead.initialNotes}
                  </p>
                </section>
              )}

              {/* Timestamps */}
              <section className="border-t pt-6">
                <h3 className="text-lg font-semibold text-tenq-gold mb-4">Timeline</h3>
                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex justify-between">
                    <span>Created:</span>
                    <span className="font-medium">{format(new Date(lead.createdAt), 'MMM dd, yyyy HH:mm')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Last Updated:</span>
                    <span className="font-medium">{format(new Date(lead.updatedAt), 'MMM dd, yyyy HH:mm')}</span>
                  </div>
                  {latestAssignment?.lastActivityAt && (
                    <div className="flex justify-between">
                      <span>Last Activity:</span>
                      <span className="font-medium">{format(new Date(latestAssignment.lastActivityAt), 'MMM dd, yyyy HH:mm')}</span>
                    </div>
                  )}
                </div>
              </section>
            </>
          )}

          {/* HISTORY TAB */}
          {activeTab === 'history' && (
            <section>
              <h3 className="text-lg font-semibold text-tenq-gold mb-4">Lead History</h3>
              {loadingHistory ? (
                <div className="flex justify-center py-8">
                  <Spinner size="md" />
                </div>
              ) : history.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <svg className="w-12 h-12 mx-auto mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  <p>No history available</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {history.map((item, index) => (
                    <div key={item.id} className="border-l-4 border-tenq-gold pl-4 py-3 bg-gray-50 rounded-r">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          {item.changeType === 'status_change' ? (
                            <div>
                              <p className="font-medium text-gray-900">Status Change</p>
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-sm px-2 py-1 rounded" style={{ backgroundColor: getStatusColor(item.oldValue?.status), color: 'white' }}>
                                  {getStatusLabel(item.oldValue?.status)}
                                </span>
                                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                                <span className="text-sm px-2 py-1 rounded" style={{ backgroundColor: getStatusColor(item.newValue?.status), color: 'white' }}>
                                  {getStatusLabel(item.newValue?.status)}
                                </span>
                              </div>
                            </div>
                          ) : (
                            <div>
                              <p className="font-medium text-gray-900 capitalize">{item.changeType?.replace(/_/g, ' ')}</p>
                            </div>
                          )}
                          {item.user && (
                            <p className="text-xs text-gray-500 mt-2">
                              by {item.user.fullName} ({item.user.nickname})
                            </p>
                          )}
                        </div>
                        <div className="text-right ml-4">
                          <p className="text-xs text-gray-500 whitespace-nowrap">
                            {format(new Date(item.timestamp), 'MMM dd, yyyy')}
                          </p>
                          <p className="text-xs text-gray-400 whitespace-nowrap">
                            {format(new Date(item.timestamp), 'HH:mm:ss')}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}

          {/* NOTES TAB */}
          {activeTab === 'notes' && (
            <section>
              <h3 className="text-lg font-semibold text-tenq-gold mb-4">Notes</h3>

              {/* Initial Notes */}
              {lead.initialNotes && (
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Initial Notes</h4>
                  <div className="card bg-blue-50 border border-blue-200">
                    <p className="text-gray-900 whitespace-pre-wrap">{lead.initialNotes}</p>
                    <div className="flex items-center justify-between text-xs text-gray-500 mt-2">
                      <span>Added on lead creation</span>
                      <span>{format(new Date(lead.createdAt), 'MMM dd, yyyy HH:mm')}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Add Note Form */}
              <div className="mb-6 space-y-3">
                <h4 className="text-sm font-semibold text-gray-700">Add New Note</h4>
                <textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  className="input min-h-[100px]"
                  placeholder="Add a note about this lead..."
                  disabled={submittingNote}
                />
                <Button
                  variant="primary"
                  onClick={handleAddNote}
                  loading={submittingNote}
                  disabled={!newNote.trim()}
                >
                  Add Note
                </Button>
              </div>

              {/* Notes List */}
              {loadingNotes ? (
                <div className="flex justify-center py-8">
                  <Spinner size="md" />
                </div>
              ) : notes.length === 0 && !lead.initialNotes ? (
                <div className="text-center py-8 text-gray-500">
                  <svg className="w-12 h-12 mx-auto mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"/>
                  </svg>
                  <p>No notes yet</p>
                  <p className="text-sm mt-1">Add your first note above</p>
                </div>
              ) : notes.length > 0 ? (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Activity Notes</h4>
                  <div className="space-y-4">
                    {notes.map((note) => (
                      <div key={note.id} className="card bg-gray-50 relative">
                        <button
                          onClick={() => {
                            setNoteToDelete(note);
                            setShowDeleteNoteConfirmation(true);
                          }}
                          className="absolute top-3 right-3 text-red-500 hover:text-red-700 transition-colors"
                          title="Delete note"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                        <p className="text-gray-900 whitespace-pre-wrap mb-2 pr-8">{note.content}</p>
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <span>
                            {note.user?.fullName} ({note.user?.nickname})
                          </span>
                          <span>{format(new Date(note.createdAt), 'MMM dd, yyyy HH:mm')}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </section>
          )}
        </div>

        {/* Status Update Section */}
        <div className="border-t bg-tenq-gray-light">
          <div className="p-6">
            <h3 className="text-lg font-semibold text-tenq-gold mb-4">Update Status</h3>
            <div className="flex flex-col gap-3">
              {getAvailableStatuses(status, latestAssignment?.appointmentDate).map(statusOption => (
                <button
                  key={statusOption}
                  onClick={() => {
                    if (statusOption === 'appt_set') {
                      setAppointmentAction('appt_set');
                      setShowAppointmentForm(true);
                    } else if (statusOption === 'reschedule_appointment') {
                      setAppointmentAction('reschedule_appointment');
                      setShowAppointmentForm(true);
                    } else if (statusOption === 'pending_booking') {
                      setShowBookingForm(true);
                    } else if (statusOption === 'booked') {
                      setShowBookingConfirmation(true);
                    } else {
                      handleStatusUpdate(statusOption);
                    }
                  }}
                  className="w-full px-4 py-2.5 text-sm font-medium text-white rounded-md transition-all hover:opacity-90"
                  style={{ backgroundColor: getStatusColor(statusOption) }}
                >
                  {getStatusLabel(statusOption)}
                </button>
              ))}
              {(isManager || isLeader) && status && !['booked', 'system_discard', 'manual_discard'].includes(status) && (
                <button
                  onClick={() => setShowDiscardConfirmation(true)}
                  className="w-full px-4 py-2.5 text-sm font-medium text-white rounded-md transition-all hover:opacity-90"
                  style={{ backgroundColor: getStatusColor('manual_discard') }}
                >
                  MANUAL DISCARD
                </button>
              )}
              {isManager && status && status !== 'new' && !['system_discard', 'manual_discard'].includes(status) && (
                <button
                  onClick={() => setShowResetToNewConfirmation(true)}
                  className="w-full px-4 py-2.5 text-sm font-medium text-white bg-red-600 rounded-md transition-all hover:opacity-90"
                >
                  RESET TO NEW
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Appointment Form Modal */}
        {showAppointmentForm && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60]">
            <div className="bg-white p-6 rounded-lg w-[90%] max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
              <h3 className="mt-0 mb-6 text-lg font-semibold">
                {status === 'appt_set' ? 'Reschedule Appointment' : 'Set Appointment'}
              </h3>
              <div className="mb-4">
                <label className="block mb-2 font-medium text-gray-700">Appointment Date & Time</label>
                <div className="flex flex-wrap gap-2 mb-3">
                  <button onClick={() => setAppointmentDate(getTodayWithTime())} className="bg-gray-200 text-gray-700 px-4 py-2 border-none rounded cursor-pointer text-sm transition-colors hover:bg-gray-300">Today</button>
                  <button onClick={() => setAppointmentDate(getTomorrowWithTime())} className="bg-gray-200 text-gray-700 px-4 py-2 border-none rounded cursor-pointer text-sm transition-colors hover:bg-gray-300">Tomorrow</button>
                  <button onClick={() => setAppointmentDate(getNextWeekWithTime())} className="bg-gray-200 text-gray-700 px-4 py-2 border-none rounded cursor-pointer text-sm transition-colors hover:bg-gray-300">Next Week</button>
                </div>
                <DatePicker
                  selected={appointmentDate}
                  onChange={(date) => setAppointmentDate(date)}
                  showTimeSelect
                  dateFormat="MMMM d, yyyy h:mm aa"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg"
                />
              </div>
              <div className="mb-4">
                <label className="block mb-2 font-medium text-gray-700">Notes</label>
                <textarea
                  value={appointmentNotes}
                  onChange={e => setAppointmentNotes(e.target.value)}
                  placeholder="Add notes..."
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg min-h-[100px]"
                />
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <Button variant="primary" onClick={handleAppointmentSubmit}>
                  Confirm
                </Button>
                <Button variant="secondary" onClick={() => setShowAppointmentForm(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Booking Form Modal */}
        {showBookingForm && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60]">
            <div className="bg-white p-6 rounded-lg w-[90%] max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
              <h3 className="mt-0 mb-6 text-lg font-semibold">Set Pending Booking Date</h3>
              <div className="mb-4">
                <label className="block mb-2 font-medium text-gray-700">Expected Booking Date</label>
                <div className="flex flex-wrap gap-2 mb-3">
                  <button onClick={() => setBookingDate(new Date())} className="bg-gray-200 text-gray-700 px-4 py-2 border-none rounded cursor-pointer text-sm transition-colors hover:bg-gray-300">Today</button>
                  <button onClick={() => {
                    const tomorrow = new Date();
                    tomorrow.setDate(tomorrow.getDate() + 1);
                    setBookingDate(tomorrow);
                  }} className="bg-gray-200 text-gray-700 px-4 py-2 border-none rounded cursor-pointer text-sm transition-colors hover:bg-gray-300">Tomorrow</button>
                  <button onClick={() => {
                    const nextWeek = new Date();
                    nextWeek.setDate(nextWeek.getDate() + 7);
                    setBookingDate(nextWeek);
                  }} className="bg-gray-200 text-gray-700 px-4 py-2 border-none rounded cursor-pointer text-sm transition-colors hover:bg-gray-300">Next Week</button>
                </div>
                <DatePicker
                  selected={bookingDate}
                  onChange={(date) => setBookingDate(date)}
                  dateFormat="MMMM d, yyyy"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg"
                />
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <Button variant="primary" onClick={handlePendingBookingSubmit}>
                  Confirm
                </Button>
                <Button variant="secondary" onClick={() => setShowBookingForm(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Booking Confirmation Modal */}
        {showBookingConfirmation && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60]">
            <div className="bg-white p-6 rounded-lg max-w-sm text-center shadow-xl" onClick={(e) => e.stopPropagation()}>
              <h3 className="mb-4 text-lg text-gray-900">Confirm Booking</h3>
              <p className="mb-6 text-gray-700 leading-relaxed">
                Are you sure you want to mark this lead as "Booked"?
              </p>
              <div className="flex justify-center gap-3">
                <Button variant="primary" onClick={() => { handleStatusUpdate('booked'); setShowBookingConfirmation(false); }}>
                  Confirm
                </Button>
                <Button variant="secondary" onClick={() => setShowBookingConfirmation(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Discard Confirmation Modal */}
        {showDiscardConfirmation && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60]">
            <div className="bg-white p-6 rounded-lg max-w-sm text-center shadow-xl" onClick={(e) => e.stopPropagation()}>
              <h3 className="mb-4 text-lg text-gray-900">Confirm Manual Discard</h3>
              <p className="mb-6 text-gray-700 leading-relaxed">
                Are you sure you want to discard this lead? This action cannot be undone.
              </p>
              <div className="flex justify-center gap-3">
                <Button variant="danger" onClick={() => { handleStatusUpdate('manual_discard'); setShowDiscardConfirmation(false); }}>
                  Discard
                </Button>
                <Button variant="secondary" onClick={() => setShowDiscardConfirmation(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Reset to New Confirmation Modal */}
        {showResetToNewConfirmation && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60]">
            <div className="bg-white p-6 rounded-lg max-w-sm text-center shadow-xl" onClick={(e) => e.stopPropagation()}>
              <h3 className="mb-4 text-lg text-gray-900">Reset Lead to NEW Status</h3>
              <p className="mb-6 text-gray-700 leading-relaxed">
                Warning: This will reset the lead status back to NEW from <strong>{getStatusLabel(status)}</strong>. This is for special cases only. Are you sure you want to proceed?
              </p>
              <div className="flex justify-center gap-3">
                <Button variant="danger" onClick={handleResetToNew}>
                  Reset to NEW
                </Button>
                <Button variant="secondary" onClick={() => setShowResetToNewConfirmation(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Note Confirmation Modal */}
        {showDeleteNoteConfirmation && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60]">
            <div className="bg-white p-6 rounded-lg max-w-sm text-center shadow-xl" onClick={(e) => e.stopPropagation()}>
              <h3 className="mb-4 text-lg text-gray-900">Delete Note</h3>
              <p className="mb-6 text-gray-700 leading-relaxed">
                Are you sure you want to delete this note? This action cannot be undone.
              </p>
              <div className="flex justify-center gap-3">
                <Button variant="danger" onClick={handleDeleteNote}>
                  Delete
                </Button>
                <Button variant="secondary" onClick={() => { setShowDeleteNoteConfirmation(false); setNoteToDelete(null); }}>
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Actions Footer */}
        <div className="sticky bottom-0 bg-white border-t p-4 flex gap-3 shadow-lg">
          {/* Show Create Sales Form button only after agent marks as booked */}
          {status === 'booked' && (
            <Button
              variant="primary"
              className="flex-1 bg-green-600 hover:bg-green-700 border-green-600"
              onClick={() => {
                navigate(`/sales-forms/new?leadId=${lead.id}&assignmentId=${latestAssignment.id}`);
                onClose();
              }}
            >
              <svg className="w-5 h-5 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Create Sales Form
            </Button>
          )}
          {onEdit && (
            <Button
              variant="primary"
              className="flex-1"
              onClick={() => {
                onEdit(lead);
                onClose();
              }}
            >
              Edit Lead
            </Button>
          )}
          <Button
            variant="secondary"
            onClick={onClose}
          >
            Close
          </Button>
        </div>
      </div>
    </>
  );
}

export default LeadDetailPane;
